import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { newsArticles } from "@/mocks/news";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = newsArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!article) {
    return <Navigate to="/hacking-news" replace />;
  }

  const related = newsArticles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    url: `${siteUrl}/hacking-news/${article.slug}`,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      "@type": "Organization",
      name: "Hackers Lodge",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Hacking News", item: `${siteUrl}/hacking-news` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title={`${article.title} — Hackers Lodge Hacking News`}
        description={article.excerpt}
        keywords={`${article.category}, hacking news, cybersecurity, ${article.tags?.join(", ") || "cybersecurity news, hacking tools"}`}
        canonicalUrl={`${siteUrl}/hacking-news/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        author={article.author}
        publishedTime={article.date}
        schema={articleSchema}
        breadcrumbSchema={breadcrumbSchema}
      />
      <Header />

      {/* Page Header Banner */}
      <div className="pt-20 md:pt-24 bg-[#333] text-white text-center py-6">
        <p className="text-lg font-medium tracking-wide">HACKING NEWS</p>
      </div>

      <main className="py-8 md:py-12">
        <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Hacking News", to: "/hacking-news" }, { label: article.title }]} />
          {/* Article Header */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
              <span className="uppercase font-semibold">{article.category}</span>
              <span>·</span>
              <span><i className="far fa-comment mr-1" />{article.comments === 0 ? "No Comments" : `${article.comments} Comment${article.comments > 1 ? "s" : ""}`}</span>
              <span>·</span>
              <span>by <strong>{article.author}</strong></span>
              <span>·</span>
              <span>{article.date}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-semibold text-gray-200 leading-tight mb-6">
              {article.title}
            </h1>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover rounded"
            />
          </div>

          {/* Article Body */}
          <div className="prose max-w-none">
            {article.content.map((paragraph, i) => (
              <p key={i} className="text-sm md:text-base text-gray-300 leading-relaxed mb-5">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Related Articles */}
          {related.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-700">
              <h3 className="text-lg font-semibold text-gray-200 mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/hacking-news/${r.slug}`}
                    className="group"
                  >
                    <div className="h-36 overflow-hidden rounded mb-3">
                      <img
                        src={r.image}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h4 className="text-sm font-semibold text-gray-200 group-hover:text-[#5bc0de] transition-colors leading-snug">
                      {r.title}
                    </h4>
                    <span className="text-xs text-gray-400 mt-1 block">{r.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to News */}
          <div className="mt-10">
            <Link
              to="/hacking-news"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              <i className="fas fa-arrow-left" /> Back to Hacking News
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}